import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Stack,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  InputAdornment,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  NoteAlt,
  Search,
  Refresh,
  Add,
  Cancel,
  Delete,
  Edit,
  RestoreFromTrash,
} from '@mui/icons-material';
import MainText from '../../components/MainText';
import CustomSwitch from '../../components/CustomSwitch';
import CleanningButton from '../../components/CleanningButton';
import CustomModal from '../../components/CustomModal';
import CustomSnackBar from '../../components/CustomSnackBar';
import AutoCompleteBank from '../../components/AutoCompleteBank';
import DatePickV2 from '../../components/DatePickV2';
import { RBAC } from '../../services/api';
import { useDispatch } from 'react-redux';
import { addTab } from '../../tabs/tabsSlice';
import { useAuth } from '../../auth/AuthProvider';

const getErrorMessage = (err, fallback) => {
  if (!err?.response) {
    const msg = err?.message || '';
    if (typeof msg === 'string' && msg.toLowerCase().includes('network')) {
      return 'Error de red. Verifica tu conexion.';
    }
    if (err?.code === 'ERR_NETWORK') {
      return 'Error de red. Verifica tu conexion.';
    }
    return fallback;
  }

  const data = err?.response?.data;
  if (typeof data === 'string') {
    const isHtml = /<\s*!doctype|<\s*html|<\s*body|<\s*table|<\s*pre/i.test(data);
    if (isHtml) {
      if (err?.response?.status === 413) {
        return 'El archivo es demasiado grande (413)';
      }
      return 'Error interno del servidor. Revisa los logs.';
    }
    return data;
  }
  if (!data) return fallback;
  if (data.detail) return data.detail;
  if (data.message) {
    if (typeof data.message === 'string') return data.message;
    if (typeof data.message === 'object') {
      const key = Object.keys(data.message)[0];
      const val = data.message?.[key];
      if (Array.isArray(val)) return val.join(' ');
      if (typeof val === 'string') return val;
    }
  }
  const key = Object.keys(data)[0];
  if (key) {
    const val = data[key];
    if (Array.isArray(val)) return val.join(' ');
    if (typeof val === 'string') return val;
  }
  return fallback;
};

const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleString('es-419', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return '-';
  }
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('es-419', {
      year: 'numeric', month: '2-digit', day: '2-digit'
    });
  } catch {
    return '-';
  }
};

const normalizeDate = (value) => {
  if (!value) return null;
  if (typeof value === 'string') {
    if (value.includes('T')) return value.split('T')[0];
    return value;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().split('T')[0];
};

const STATUS_OPTIONS = [
  { value: 'active', label: 'Activas' },
  { value: 'inactive', label: 'Inactivas' },
  { value: 'all', label: 'Todas' },
];

const buildInitialFormState = (initial) => {
  const hasComment = typeof initial?.comment !== 'undefined';
  const normalizedDate = normalizeDate(initial?.date) || new Date().toISOString().split('T')[0];
  return {
    number: initial?.number || '',
    logical_warehouse_id: initial?.logical_warehouse?.id || initial?.logical_warehouse_id || null,
    date: normalizedDate,
    is_active: initial?.is_active ?? true,
    comment: hasComment ? (initial?.comment || '') : '',
  };
};

function ReceiveNoteForm({ initial, onSubmit, logicalWarehouses = [] }) {
  const hasComment = typeof initial?.comment !== 'undefined';
  const [form, setForm] = useState(() => buildInitialFormState(initial));

  useEffect(() => {
    setForm(buildInitialFormState(initial));
  }, [initial]);

  const selectedLogical = useMemo(
    () => (logicalWarehouses || []).find((l) => String(l.id) === String(form.logical_warehouse_id)) || null,
    [logicalWarehouses, form.logical_warehouse_id]
  );

  const handleChange = (name, value) => {
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLogicalChange = (_, value) => {
    setForm((prev) => ({
      ...prev,
      logical_warehouse_id: value ? value.id : null,
    }));
  };

  const handleSave = () => {
    const number = parseInt(form.number, 10);
    if (!number || number <= 0) {
      onSubmit({ error: 'El numero debe ser mayor a 0' });
      return;
    }
    if (!form.logical_warehouse_id) {
      onSubmit({ error: 'Bodega logica requerida' });
      return;
    }
    const dateStr = normalizeDate(form.date);
    if (!dateStr) {
      onSubmit({ error: 'Fecha invalida' });
      return;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(`${dateStr}T00:00:00`);
    if (selectedDate > today) {
      onSubmit({ error: 'La fecha no puede ser futura' });
      return;
    }

    const payload = {
      number,
      logical_warehouse_id: form.logical_warehouse_id,
      date: dateStr,
      is_active: !!form.is_active,
    };
    if (hasComment) payload.comment = form.comment;
    onSubmit(payload);
  };

  return (
    <Stack spacing={2} sx={{ p: 1 }}>
      <TextField
        size="small"
        label="Numero"
        type="number"
        value={form.number}
        onChange={(e) => handleChange('number', e.target.value)}
      />
      <AutoCompleteBank
        options={logicalWarehouses}
        getOptionLabel={(option) => option.name}
        onChange={handleLogicalChange}
        name="logical_warehouse"
        value={selectedLogical}
        label='Bodega logica'
        disabled={(logicalWarehouses || []).length === 0}
      />
      {(logicalWarehouses || []).length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No hay bodegas logicas disponibles
        </Typography>
      )}
      <DatePickV2
        allowFutureDates={false}
        allowHistoricalDates={true}
        label='Fecha nota'
        value={form.date}
        size='small'
        name={'date'}
        onChange={handleChange}
        returnType='iso'
      />
      {hasComment && (
        <TextField
          size="small"
          label="Comentario"
          value={form.comment}
          onChange={(e) => handleChange('comment', e.target.value)}
          multiline
          minRows={3}
        />
      )}
      <FormControlLabel
        control={
          <CustomSwitch
            name="is_active"
            label={form.is_active ? 'Activa' : 'Inactiva'}
            initialValue={form.is_active}
            onChange={(_, val) => handleChange('is_active', val)}
          />
        }
        label=""
      />
      <Box sx={{ textAlign: 'right' }}>
        <Button variant="contained" onClick={handleSave}>Guardar</Button>
      </Box>
    </Stack>
  );
}

export default function ReceiveNotesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ search: '', status: 'active' });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [logicalWarehouses, setLogicalWarehouses] = useState([]);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSev, setSnackSev] = useState('info');
  const dispatch = useDispatch();
  const { company } = useAuth();

  useEffect(() => {
    dispatch(addTab({ label: 'Notas de entrada', path: '/catalogs/receive-notes' }));
  }, [dispatch]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(filters.search || ''), 450);
    return () => clearTimeout(t);
  }, [filters.search]);

  const handleFilter = (name, value) => setFilters((v) => ({ ...v, [name]: value }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status === 'active') params.is_active = 'true';
      if (filters.status === 'inactive') params.is_active = 'false';
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await RBAC.receiveNotes.list(params);
      const data = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      setRows(data.map((r) => ({ id: r.id, ...r })));
    } catch (e) {
      setRows([]);
      setSnackMsg(getErrorMessage(e, 'No se pudieron cargar las notas de entrada'));
      setSnackSev('error');
      setSnackOpen(true);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, filters.status]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const fetchLogical = async () => {
      try {
        const params = company?.id ? { company: company.id } : undefined;
        const res = await RBAC.logicalWarehouses.list(params);
        const list = Array.isArray(res.data) ? res.data : (res.data?.results || []);
        setLogicalWarehouses(list);
      } catch {
        setLogicalWarehouses([]);
      }
    };
    fetchLogical();
  }, [company?.id]);

  const openCreate = () => { setEditRow(null); setDialogOpen(true); };
  const openEdit = (row) => { setEditRow(row ? { ...row } : null); setDialogOpen(true); };

  const handleSubmit = async (payload) => {
    if (payload?.error) {
      setSnackMsg(payload.error);
      setSnackSev('error');
      setSnackOpen(true);
      return;
    }
    try {
      if (editRow) await RBAC.receiveNotes.update(editRow.id, payload);
      else await RBAC.receiveNotes.create(payload);
      setDialogOpen(false);
      setEditRow(null);
      load();
      setSnackMsg(editRow ? 'Nota actualizada' : 'Nota creada');
      setSnackSev('success');
      setSnackOpen(true);
    } catch (e) {
      setSnackMsg(getErrorMessage(e, 'No se pudo guardar la nota'));
      setSnackSev('error');
      setSnackOpen(true);
    }
  };

  const toggleActive = async (row) => {
    try {
      await RBAC.receiveNotes.toggleActive(row.id, !row.is_active);
      load();
      setSnackMsg(row.is_active ? 'Nota desactivada' : 'Nota activada');
      setSnackSev('success');
      setSnackOpen(true);
    } catch (e) {
      setSnackMsg(getErrorMessage(e, 'No se pudo actualizar el estado'));
      setSnackSev('error');
      setSnackOpen(true);
    }
  };

  const removeRow = async (row) => {
    if (!window.confirm('Eliminar nota permanentemente?')) return;
    try {
      await RBAC.receiveNotes.remove(row.id);
      load();
      setSnackMsg('Nota eliminada');
      setSnackSev('success');
      setSnackOpen(true);
    } catch (e) {
      setSnackMsg(getErrorMessage(e, 'No se pudo eliminar la nota'));
      setSnackSev('error');
      setSnackOpen(true);
    }
  };

  const columns = useMemo(() => ([
    {
      field: 'number',
      headerName: 'Numero',
      width: 120,
      renderCell: ({ value }) => <Typography sx={{ fontWeight: 700 }}>{value ?? '-'}</Typography>,
    },
    {
      field: 'logical_warehouse',
      headerName: 'Bodega',
      flex: 1,
      minWidth: 220,
      valueGetter: (p) => p?.row?.logical_warehouse?.name || p?.row?.warehouse || '-',
    },
    {
      field: 'date',
      headerName: 'Fecha',
      width: 130,
      renderCell: ({ value }) => <Typography variant="body2">{formatDate(value)}</Typography>,
    },
    {
      field: 'is_active',
      headerName: 'Estado',
      width: 120,
      renderCell: ({ value }) => (
        <Chip
          size="small"
          label={value ? 'Activa' : 'Inactiva'}
          color={value ? 'success' : 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'invoices_count',
      headerName: '# Facturas',
      width: 110,
      renderCell: ({ value }) => <Typography variant="body2">{value ?? 0}</Typography>,
    },
    {
      field: 'createdAt',
      headerName: 'Creada',
      width: 180,
      renderCell: ({ value }) => <Typography variant="caption">{formatDateTime(value)}</Typography>,
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 200,
      sortable: false,
      renderCell: (params) => {
        const row = params?.row;
        if (!row) return null;
        return (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Ver / Editar">
              <IconButton size="small" onClick={() => openEdit(row)}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            {row.is_active ? (
              <Tooltip title="Desactivar">
                <IconButton size="small" onClick={() => toggleActive(row)}>
                  <Cancel fontSize="small" color="warning" />
                </IconButton>
              </Tooltip>
            ) : (
              <>
                <Tooltip title="Restaurar">
                  <IconButton size="small" color="success" onClick={() => toggleActive(row)}>
                    <RestoreFromTrash fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Eliminar permanentemente">
                  <IconButton size="small" color="error" onClick={() => removeRow(row)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Stack>
        );
      },
    },
  ]), []);

  const clearAll = () => setFilters({ search: '', status: 'active' });

  return (
    <Box sx={{ width: '100%' }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2, gap: 2 }}
      >
        <MainText title="Notas de entrada" subtitle="Catalogo por empresa activa." icon={NoteAlt} width="auto" />
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {filters.search && (
              <Chip label={`Busqueda: "${filters.search}"`} onDelete={() => handleFilter('search', '')} />
            )}
            {filters.status === 'inactive' && (
              <Chip label="Inactivas" color="error" onDelete={() => handleFilter('status', 'active')} />
            )}
            {filters.status === 'all' && (
              <Chip label="Todas" color="info" onDelete={() => handleFilter('status', 'active')} />
            )}
          </Box>
          <CleanningButton onClick={clearAll} />
        </Stack>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center" gap={1.5} sx={{ mb: 1 }}>
        <TextField
          size="small"
          placeholder="Buscar numero o bodega..."
          value={filters.search}
          onChange={(e) => handleFilter('search', e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: { xs: '100%', md: 320 } }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Estado</InputLabel>
          <Select
            label="Estado"
            value={filters.status}
            onChange={(e) => handleFilter('status', e.target.value)}
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Refrescar"><IconButton onClick={load}><Refresh /></IconButton></Tooltip>
          <Button
            onClick={openCreate}
            startIcon={<Add />}
            size="small"
            variant="contained"
            sx={{ borderRadius: 2, px: 1.5, textTransform: 'none', fontWeight: 700, boxShadow: 'none', '&:hover': { boxShadow: 'none', transform: 'translateY(-1px)' } }}
          >
            Crear
          </Button>
        </Box>
      </Stack>

      <Box sx={{ height: 460, width: '100%' }}>
        <DataGrid
          rows={rows || []}
          columns={columns}
          loading={loading}
          disableColumnFilter
          disableColumnMenu
          pageSizeOptions={[10, 20, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
          rowHeight={52}
          columnHeaderHeight={50}
          sx={{
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#10173c', color: '#fff' },
            '& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:hover': {
              backgroundColor: '#10173c !important',
            },
            '& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-menuIcon, & .MuiDataGrid-columnSeparator': { color: '#ffffff !important' },
          }}
        />
      </Box>

      <CustomModal
        open={dialogOpen}
        setOpen={(v) => { setDialogOpen(v); if (!v) setEditRow(null); }}
        title={editRow ? 'Editar nota' : 'Crear nota'}
        component={ReceiveNoteForm}
        componentProps={{ key: editRow?.id || 'new', initial: editRow, onSubmit: handleSubmit, logicalWarehouses }}
        width="520px"
      />

      <CustomSnackBar open={snackOpen} setOpen={setSnackOpen} message={snackMsg} severity={snackSev} />
    </Box>
  );
}
